"use strict";
/* global $, dat */

import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { RGBELoader } from "three/addons/loaders/RGBELoader.js";

import { GUI } from "three/addons/libs/lil-gui.module.min.js";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";

import { SAOPass } from "three/addons/postprocessing/SAOPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { FXAAShader } from "three/addons/shaders/FXAAShader.js";

import { RectAreaLightUniformsLib } from "three/addons/lights/RectAreaLightUniformsLib.js";

import Stats from "three/addons/libs/stats.module.js";
import { TONE_MAPPING_EXPOSURE, ENVIRONMENT_MAP_INTENSITY, ENVIRONMENT_MAP, getInitialModelIndex } from "./settings.js";
import { initRaycast } from "./raycast.js";
import { currentOS, hotspots, updateHotspots } from "./3d-configurator.js";

const TEST_MODE = false;
const DEV_GUI_MODE = false;
const DEV_USE_STATS = false;
let startPosition = new THREE.Vector3(1.275, 1.1, -4.76);

const correctionFloor = 0;
export let floor;
let floorMaterial;


const isLowPerformance = isWeakDevice();
let renderRequested = false;

export function requestRender() {
  if (!renderRequested) {
    renderRequested = true;
  }
}

function isWeakDevice() {
  if (navigator.hardwareConcurrency < 4) return true;

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return true;

    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    if (dbg) {
      const renderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);

      if (!/Intel/i.test(renderer)) return false;

      const isModernIntel = /Iris\s?Xe/i.test(renderer) || /Arc/i.test(renderer);
      if (isModernIntel) return false;

      const isLegacyIntel = /HD\s?Graphics/i.test(renderer) ||
        /UHD\s?Graphics\s?6/i.test(renderer) ||
        (/Iris/i.test(renderer) && !/Xe/i.test(renderer));

      if (isLegacyIntel) return true;
    }
  } catch (e) {
    return false;
  }

  return false;
}

export function requestDelayedRender() {
  requestRender();
  setTimeout(requestRender, 50);
  setTimeout(requestRender, 100);
  setTimeout(requestRender, 500);
  setTimeout(requestRender, 1000);
}

function resizeRendererToDisplaySize(renderer) {
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio;
  const width = Math.round(rect.width * dpr);
  const height = Math.round(rect.height * dpr);
  const needResize = renderer.domElement.width !== width
    || renderer.domElement.height !== height;

  return needResize;
}

export function updateRenderSize(forceResize = false) {
  if (!renderer) return;

  const rect = canvas.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const dpr = window.devicePixelRatio;
  // renderer.domElement.width is physical px (setSize multiplies by pixelRatio internally)
  const physicalWidth = Math.round(rect.width * dpr);
  const physicalHeight = Math.round(rect.height * dpr);
  const needResize = renderer.domElement.width !== physicalWidth
    || renderer.domElement.height !== physicalHeight;

  if (needResize || forceResize) {
    // pass CSS pixels — setSize multiplies by pixelRatio internally
    renderer.setSize(rect.width, rect.height, false);
    if (composer) {
      composer.setSize(rect.width, rect.height);
    }
    camera.aspect = rect.width / rect.height;
    camera.updateProjectionMatrix();
    controls.update();
  }
}

export let scene;
export let sceneWatarmark;
export let renderer;
export let canvas;
export let camera;
export let controls;
export let envMap;
let envYOffset = 0; // vertical shift of the environment map horizon
export let composer;
export let renderScale = 1.0;
export let renderAntialias = true;

var renderPass;

export var outputPass;
export var bloomPass;
export var fxaaPass;
export var saoPass;

let stats;

export var params_Bloom = {
  threshold: 1,
  strength: 1,
  radius: 1,
  exposure: 1,
};

export const IMPORTED_MODELS = [];
export const IMPORTED_MODELS_GLTF = [];
export let TABLES_GLTF = null;
export let HEATER_PHOENIX_GLTF = null;
export let HEATER_PALMA_GLTF = null;
export let isModelsLoaded = false;

const scenePropertiesDefault = {
  BACKGROUND_COLOR: 0xffffff,
  MODEL_PATHS: ["./src/models/empty_scene.glb"],
  ENVIRONMENT_MAP: ENVIRONMENT_MAP,
  ENVIRONMENT_MAP_INTENSITY: ENVIRONMENT_MAP_INTENSITY,
  SHADOW_TRANSPARENCY: 0.0,
  MODEL_CENTER_POSITION: 0,
};

export function updateBloomSettings(
  threshold = null,
  strength = null,
  radius = null
) {
  if (bloomPass == null) {
    return;
  }

  if (threshold != null) {
    params_Bloom.threshold = threshold;
  }
  if (strength != null) {
    params_Bloom.strength = strength;
  }
  if (radius != null) {
    params_Bloom.radius = radius;
  }

  bloomPass.threshold = params_Bloom.threshold;
  bloomPass.strength = params_Bloom.strength;
  bloomPass.radius = params_Bloom.radius;
}

export let dirLight;
let fillLightFront, fillLightBack;
// let pointLight, pointLight2;
export let pointLightIntensity, dirLightIntencity;

export function Get3DScene() {
  return scene;
}

// PMREM env map from a sphere-pair scene so the horizon Y can be shifted.
// sphere 1: full sphere, scale.x=-1 (inside-out), carries the HDR texture
// sphere 2: scale.y=0 (flat disk) moved to yOffset — shifts where the horizon sits
function buildEnvMapFromSpheres(texture, pmremGenerator, yOffset = 0) {
  void yOffset;
  const envScene = new THREE.Scene();
  const geo = new THREE.SphereGeometry(50, 64, 32);
  const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.BackSide });
  const sphere = new THREE.Mesh(geo, mat);
  sphere.scale.x = -1;
  envScene.add(sphere);
  return pmremGenerator.fromScene(envScene).texture;
}

// Call this to shift the env map horizon and re-apply it to the scene.
// yOffset > 0 → horizon moves up (more sky in reflections)
// yOffset < 0 → horizon moves down (more ground)
export function setEnvMapY(yOffset) {
  envYOffset = yOffset;
  if (!scene || !renderer) return;
  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  const rgbeLoader = new RGBELoader();
  const path = scene._envMapPath;
  if (!path) return;
  rgbeLoader.load(path, (texture) => {
    texture.mapping = THREE.EquirectangularReflectionMapping;
    if (envMap) envMap.dispose();
    envMap = buildEnvMapFromSpheres(texture, pmremGenerator, yOffset);
    scene.environment = envMap;
    texture.dispose();
    pmremGenerator.dispose();
  });
}

// eslint-disable-next-line no-unused-vars
export function updateEnvMap(
  path,
  intensity,
  onlyMap = false,
  updateSceneBackground = false
) {
  if (path == null) {
    return;
  }

  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  pmremGenerator.compileEquirectangularShader();
  const rgbeLoader = new RGBELoader();
  rgbeLoader.load(path, function (texture) {
    texture.mapping = THREE.EquirectangularReflectionMapping;
    envMap = buildEnvMapFromSpheres(texture, pmremGenerator, envYOffset);
    scene.environment = envMap;
    scene.environmentIntensity = 1.0;

    if (updateSceneBackground) {
      scene.background = envMap;
    }

    texture.dispose();
    pmremGenerator.dispose();
  });
}

export function create3DScene(
  properties = scenePropertiesDefault,
  loadFunction
) {
  if (!properties) {
    properties = scenePropertiesDefault;
  }

  const {
    MODEL_PATHS,
    BACKGROUND_COLOR,
    ENVIRONMENT_MAP,
    SHADOW_TRANSPARENCY,
    ENVIRONMENT_MAP_INTENSITY,
    MODEL_CENTER_POSITION,
  } = properties;

  // ********************************************
  //      Init SCENE, canvas, RENDERER
  // ********************************************
  scene = new THREE.Scene();

  scene.background = new THREE.Color(BACKGROUND_COLOR);
  //scene.background = null;
  canvas = document.getElementById("ar_model_view");

  camera = new THREE.PerspectiveCamera(
    60,
    window.innerWidth / window.innerHeight,
    0.05,
    1000
  );

  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: renderAntialias,
    preserveDrawingBuffer: true,
    alpha: true,
    powerPreference: "high-performance",
    logarithmicDepthBuffer: false,
  });
  renderer.shadowMap.enabled = true;
  renderer.shadowMapSoft = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.setPixelRatio(
    (window.devicePixelRatio ? window.devicePixelRatio : 1) * renderScale
  );
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = TONE_MAPPING_EXPOSURE;
  // renderer.outputEncoding = THREE.sRGBEncoding;

  if (ENVIRONMENT_MAP != "") {
    scene._envMapPath = ENVIRONMENT_MAP;
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const rgbeLoader = new RGBELoader();
    rgbeLoader.load(ENVIRONMENT_MAP, function (texture) {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      envMap = buildEnvMapFromSpheres(texture, pmremGenerator, envYOffset);
      scene.environment = envMap;
      scene.environmentIntensity = 1.0;
      scene.environmentRotation.y = Math.PI * 5.0;
      texture.dispose();
      pmremGenerator.dispose();
      window.setEnvY = (y) => setEnvMapY(y);
    });
  }

  // ********************************************
  //              IMPORT MODELS
  // ********************************************
  async function importAllModels(array, callback) {
    const loader = new GLTFLoader();
    const priorityIndex = getInitialModelIndex();

    // Pre-fill slots so indices stay stable (IMPORTED_MODELS[i] matches MODEL_PATHS[i])
    for (let i = 0; i < array.length; i++) {
      IMPORTED_MODELS.push(null);
      IMPORTED_MODELS_GLTF.push(null);
    }

    // Load the model the user actually needs first
    const priorityGltf = await loader.loadAsync(array[priorityIndex]);
    IMPORTED_MODELS_GLTF[priorityIndex] = priorityGltf;
    onLoadModel(priorityGltf, priorityIndex);

    if (callback != null) callback();

    // Load remaining models in the background without blocking the app
    for (let i = 0; i < array.length; i++) {
      if (i === priorityIndex) continue;
      loader.loadAsync(array[i]).then(gltf => {
        IMPORTED_MODELS_GLTF[i] = gltf;
        onLoadModel(gltf, i);
        scene.add(IMPORTED_MODELS[i]);
        IMPORTED_MODELS[i].visible = false;
      }).catch(err => console.warn(`[model ${i}] failed to load:`, err));
    }
  }

  function onLoadModel(gltf, index) {
    const model = gltf.scene;

    model.traverse((o) => {
      o.layers.enable(1);
      if (o.isMesh) {
        o.visible = false;
        o.castShadow = true;
        o.receiveShadow = true;
        if (o.material.map) {
          o.material.map.anisotropy = 16;
          o.material.map.needsUpdate = true;
          o.material.needsUpdate = true;
        }
      }
    });

    const scale = 1;
    model.scale.set(scale, scale, scale);
    model.rotation.y = 0.12;
    model.position.set(-0.04, MODEL_CENTER_POSITION - 0.3, 0);
    IMPORTED_MODELS[index] = model;
  }

  function onImportModelSuccessful() {
    // Only add the priority model to the scene now; background models add themselves on load
    const priorityIndex = getInitialModelIndex();
    if (IMPORTED_MODELS[priorityIndex]) {
      IMPORTED_MODELS[priorityIndex].visible = false;
      scene.add(IMPORTED_MODELS[priorityIndex]);
    }
    // LOADER.classList.add('invisible');
    // $('.summary.entry-summary').removeClass('hidden');
    isModelsLoaded = true;

    //const box = new THREE.Box3().setFromObject( scene );
    //if(gtaoPass != null){
    //  gtaoPass.setSceneClipBox( box );
    //}

    initDevGui(
      SHADOW_TRANSPARENCY,
      TONE_MAPPING_EXPOSURE,
      IMPORTED_MODELS[getInitialModelIndex()],
    );

    loadFunction();
  }

  importAllModels(MODEL_PATHS, onImportModelSuccessful);

  {
    const tablesLoader = new GLTFLoader();
    tablesLoader.loadAsync('public/models/tables.glb').then(gltf => {
      TABLES_GLTF = gltf;
      gltf.scene.traverse(o => { if (o.isMesh) o.visible = false; });
    }).catch(err => console.warn('[tables.glb] failed to load:', err));
  }

  {
    const heaterPhoenixLoader = new GLTFLoader();
    heaterPhoenixLoader.loadAsync('public/models/heater_phoenix.glb').then(gltf => {
      HEATER_PHOENIX_GLTF = gltf;
      gltf.scene.traverse(o => { if (o.isMesh) o.visible = false; });
    }).catch(err => console.warn('[heater_phoenix.glb] failed to load:', err));
  }

  {
    const heaterPalmaLoader = new GLTFLoader();
    heaterPalmaLoader.loadAsync('public/models/heater_palma.glb').then(gltf => {
      HEATER_PALMA_GLTF = gltf;
      gltf.scene.traverse(o => { if (o.isMesh) o.visible = false; });
    }).catch(err => console.warn('[heater_palma.glb] failed to load:', err));
  }

  // ********************************************
  //                   LIGHTS
  // ********************************************
  // if (ENVIRONMENT_MAP == "") {

  // }
  const cameraSize = 18;

  dirLightIntencity = 1.5;
  dirLight = new THREE.DirectionalLight(0xffffff, dirLightIntencity);
  dirLight.position.set(26, 97.2, -57.5);
  // dirLight.castShadow = true;
  // dirLight.shadow.bias = -0.0005;
  // dirLight.shadow.normalBias = 0.02;
  // dirLight.shadow.radius = 4;

  dirLight.castShadow = true;
  dirLight.shadow.bias = -0.0005;
  dirLight.shadow.normalBias = 0.05;
  dirLight.shadow.radius = 4;
  dirLight.shadow.camera.left = -cameraSize;
  dirLight.shadow.camera.right = cameraSize;
  dirLight.shadow.camera.top = cameraSize;
  dirLight.shadow.camera.bottom = -cameraSize;
  dirLight.shadow.camera.near = 3.74;
  dirLight.shadow.camera.far = 120;
  dirLight.target.position.set(0, 0.7, 0);
  scene.add(dirLight.target);

  switch (currentOS) {
    case "Android":
      dirLight.shadow.mapSize = new THREE.Vector2(2048, 2048);
      // dirLight.shadow.radius = 2;
      dirLight.shadow.blurSamples = 10;
      break;
    case "iOS":
      dirLight.shadow.mapSize = new THREE.Vector2(2048, 2048);
      // dirLight.shadow.radius = 2;
      dirLight.shadow.blurSamples = 10;
      break;
    case "Windows":
      dirLight.shadow.mapSize = new THREE.Vector2(2048, 2048);
      // dirLight.shadow.radius = 2;
      dirLight.shadow.blurSamples = 10;
      break;
    case "Macintosh":
      dirLight.shadow.mapSize = new THREE.Vector2(2048, 2048);
      // dirLight.shadow.radius = 2;
      dirLight.shadow.blurSamples = 10;
      break;
    default:
      dirLight.shadow.mapSize = new THREE.Vector2(2048, 2048);
      // dirLight.shadow.radius = 2;
      dirLight.shadow.blurSamples = 10;
      break;
  }

  scene.add(dirLight);

  // fillLightFront = new THREE.DirectionalLight(0xffffff, 5.0);
  fillLightFront = new THREE.DirectionalLight(0xffffff, 0.2);
  // fillLightFront.position.set(0, 2.5, 20);
  fillLightFront.position.set(0, 2.5, 50);
  fillLightFront.target.position.set(0, 0.7, 0);
  scene.add(fillLightFront.target);
  scene.add(fillLightFront);

  // fillLightBack = new THREE.DirectionalLight(0xffffff, 5.0);
  fillLightBack = new THREE.DirectionalLight(0xffffff, 1.5);
  fillLightBack.position.set(0, 4, 20);
  fillLightBack.target.position.set(0, 0.7, 0);
  scene.add(fillLightBack.target);
  scene.add(fillLightBack);

  // **** RectAreaLights *******

  RectAreaLightUniformsLib.init();

  // const rectLight1 = new THREE.RectAreaLight( 0xff0000, 5, 6.03, 3.9 );
  // rectLight1.position.set( 0, 1.35, 0 );
  // rectLight1.lookAt( 0, 10, 0 );
  // scene.add( rectLight1 );
  // scene.add( new RectAreaLightHelper( rectLight1 ) );

  // **** HELPERS *******

  if (TEST_MODE) {
    const helperDir = new THREE.DirectionalLightHelper(dirLight, 2, 0xff0000);
    scene.add(helperDir);
  }

  if (TEST_MODE) {
    const axesHelper = new THREE.AxesHelper(10);
    scene.add(axesHelper);
  }

  // ********************************************
  //                  FLOOR
  // ********************************************
  const floorGeometry = new THREE.PlaneGeometry(5000, 5000, 1, 1);
  floorMaterial = new THREE.ShadowMaterial();
  floorMaterial.opacity = SHADOW_TRANSPARENCY;

  floor = new THREE.Mesh(floorGeometry, floorMaterial);
  floor.rotation.x = -0.5 * Math.PI;
  floor.receiveShadow = true;
  floor.position.y = MODEL_CENTER_POSITION + correctionFloor - 0.3;
  scene.add(floor);


  // ********************************************
  //                 CONTROLS
  // ********************************************
  controls = new OrbitControls(camera, renderer.domElement);
  controls.maxPolarAngle = Math.PI / 2; // 2.29
  controls.minAzimuthAngle = -Infinity;
  controls.maxAzimuthAngle = Infinity;
  controls.enableDamping = true;
  controls.enablePan = true;
  controls.dampingFactor = 0.2; // 0.1
  controls.autoRotateSpeed = -0.5;
  controls.maxDistance = 40;
  controls.minDistance = 1;
  controls.autoRotate = false;
  controls.enableZoom = true;

  startPosition && camera.position.copy(startPosition);
  controls.target.set(0.009, 0.7, 0.007);
  controls.update();
  controls.saveState();

  window.scene = scene;
  window.controls = controls;

  if (TEST_MODE) {
    controls.enablePan = true;
    controls.minDistance = 0.01;
  }

  controls.addEventListener("change", () => requestRender());

  controls.addEventListener("end", () => {
    controls.autoRotate = false;

    if (TEST_MODE) {
      consoleLogPosition("camera", camera.position, 3);
      consoleLogPosition("target", controls.target, 3);
    }
  });

  composer = new EffectComposer(renderer);

  renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  bloomPass = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    1.5,
    0.4,
    0.85
  );
  bloomPass.threshold = params_Bloom.threshold;
  bloomPass.strength = params_Bloom.strength;
  bloomPass.radius = params_Bloom.radius;
  bloomPass.enabled = false;

  saoPass = new SAOPass(scene, camera, false, true);
  saoPass.params.output = SAOPass.OUTPUT.Default;
  saoPass.params.saoBias = 0.5;
  saoPass.params.saoIntensity = 0.18;
  saoPass.params.saoScale = 150.0;
  saoPass.params.saoKernelRadius = 10;
  saoPass.params.saoMinResolution = 0;
  saoPass.params.saoBlur = false;
  saoPass.params.saoBlurRadius = 2;
  saoPass.params.saoBlurStdDev = 2;
  saoPass.params.saoBlurDepthCutoff = 0.01;
  saoPass.enabled = false;
  outputPass = new OutputPass();
  outputPass.enabled = false;

  fxaaPass = new ShaderPass(FXAAShader);
  fxaaPass.enabled = false;

  composer.addPass(bloomPass);
  composer.addPass(saoPass);
  composer.addPass(outputPass);
  composer.addPass(fxaaPass);

  if (DEV_USE_STATS) {
    stats = new Stats();
    document.body.appendChild(stats.dom);
    stats.domElement.style.cssText = "";
    stats.domElement.setAttribute("class", "statsClass");
  }

  // ********************************************
  //      ANIMATE
  // ********************************************
  let lastTime = 0;
  const lowPerfInterval = 1000 / 30;

  function animate(time = 0) {
    requestAnimationFrame(animate);

    if (document.hidden) return;

    let needsRender = renderRequested;

    if (controls && controls.update()) {
      needsRender = true;
    }

    if (!needsRender) return;

    if (isLowPerformance) {
      const delta = time - lastTime;
      if (delta < lowPerfInterval) return;
      lastTime = time;
    }

    updateRenderSize();

    if (renderer.domElement.width === 0 || renderer.domElement.height === 0) return;

    if (hotspots && updateHotspots) updateHotspots(hotspots);
    if (DEV_USE_STATS && stats) stats.update();

    if (composer) {
      composer.render();
    } else {
      renderer.render(scene, camera);
    }
    renderRequested = false;
  }

  function resizeRendererToDisplaySize(renderer) {
    const canvasContainer = document.getElementById("ar_model_viewer");
    const rect = canvasContainer.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    const needResize =
      renderer.domElement.width !== width ||
      renderer.domElement.height !== height;

    if (needResize) {
      renderer.setSize(width, height, false);
      if (
        composer != undefined &&
        composer != null &&
        scene != null &&
        camera != null
      ) {
        composer.setSize(width, height);
      }
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      controls.update();
    }
    return needResize;
  }

  const resizeObserver = new ResizeObserver(() => requestRender());
  resizeObserver.observe(document.getElementById("ar_model_viewer"));

  updateRenderSize(true);
  requestRender();
  animate();
  console.log(scene);
}

// ********************************************
//      Additional functions
// ********************************************
// export function getMobileOperatingSystem() {
//   const userAgent = navigator.userAgent || navigator.vendor || window.opera;

//   if (/Macintosh/i.test(userAgent)) {
//     return "Macintosh";
//   }

//   if (/Windows/i.test(userAgent) || /Win/i.test(userAgent)) {
//     return "Windows";
//   }

//   if (/windows phone/i.test(userAgent)) {
//     return "Windows Phone";
//   }

//   if (/android/i.test(userAgent)) {
//     return "Android";
//   }

//   if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
//     return "iOS";
//   }

//   return "unknown";
// }

//new
export async function getMobileOperatingSystem() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return "iOS";
  if (/visionOS|VisionPro/i.test(userAgent)) return "VisionPro";

  if (/Macintosh/i.test(userAgent) && navigator.xr) {
    try {
      const isVRSupported = await navigator.xr.isSessionSupported(
        "immersive-vr"
      );
      if (isVRSupported) {
        return "VisionPro";
      }
    } catch (error) {
      console.warn("WebXR check failed:", error);
    }
  }

  if (/android/i.test(userAgent)) return "Android";
  if (/Windows/i.test(userAgent) || /Win/i.test(userAgent)) return "Windows";
  if (/Linux/i.test(userAgent) && !/android/i.test(userAgent)) return "Linux";
  if (/Macintosh/i.test(userAgent)) return "Macintosh";

  if (isTouchDevice) return "TouchScreenDevice";
  return "unknown";
}

function consoleLogPosition(text = "", pos, num = 2) {
  let k = 1;

  for (let i = 0; i < num; i++) {
    k = k * 10;
  }

  const x = Math.round(pos.x * k) / k;
  const y = Math.round(pos.y * k) / k;
  const z = Math.round(pos.z * k) / k;

  console.log("🚀 " + text + ": [" + x + ", " + y + ", " + z + "],");
}

export function isEqualVector(vector1, vector2) {
  const precision = 3;
  console.log("🚀", vector1.x.toFixed(3), vector2.x.toFixed(3));
  console.log("🚀", vector1.y.toFixed(3), vector2.y.toFixed(3));
  console.log("🚀", vector1.z.toFixed(3), vector2.z.toFixed(3));
  return (
    vector1.x.toFixed(precision) === vector2.x.toFixed(precision) &&
    vector1.y.toFixed(precision) === vector2.y.toFixed(precision) &&
    vector1.z.toFixed(precision) === vector2.z.toFixed(precision)
  );
}

// ********************************************
//      Animation functions
// ********************************************

// ANIMATION OF MODEL - "SCALE" - appearing or disappearing
export function animateScale(
  model,
  duration = 500,
  startScale = 0,
  endScale = 1,
  timingKeyword = "ease-in",
  callback = () => springScale(model)
) {
  function timingFunction(progress) {
    switch (timingKeyword) {
      case "ease-in":
        return progress * progress;
      case "ease-out":
        return 1 - Math.pow(1 - progress, 2);
      case "ease-in-out":
        return progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      default:
        return progress;
    }
  }

  let startTime = null;

  function animate(currentTime) {
    if (!startTime) {
      startTime = currentTime;
    }

    const elapsedTime = currentTime - startTime;
    const progress = Math.min(elapsedTime / duration, 1);
    const easedProgress = timingFunction(progress);
    const interpolatedScale =
      startScale + (endScale - startScale) * easedProgress;
    model.scale.set(interpolatedScale, interpolatedScale, interpolatedScale);
    requestRender();

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      model.scale.set(endScale, endScale, endScale);
      callback();
    }
  }

  requestAnimationFrame(animate);
}

// ANIMATION OF MODEL - "SPRING-SCALE"
export function springScale(
  model,
  duration = 500,
  oscillations = 1,
  callback = () => { }
) {
  const startTime = performance.now();
  const startScale = model.scale.x;
  const dampingFactor = 0.1; // attenuation coefficient
  const maxAmplitude = 0.2 * startScale; // maximum oscillation amplitude (20%)

  function animate(currentTime) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const angularFrequency = (oscillations * Math.PI * 2) / duration;
    const amplitude =
      maxAmplitude * Math.pow(dampingFactor, elapsed / duration);
    const phase = angularFrequency * elapsed;
    const currentScale = startScale + amplitude * Math.sin(phase);

    model.scale.set(currentScale, currentScale, currentScale);
    requestRender();

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      model.scale.set(startScale, startScale, startScale);
      callback();
    }
  }

  requestAnimationFrame(animate);
}

//#region DEVELOPER GUI

function initDevGui(SHADOW_TRANSPARENCY, toneMappingExposure, model) {
  if (!DEV_GUI_MODE) return;

  const gui = new GUI({ title: 'Dev Controls' });

  const guiParams = {
    shadowTransparency: SHADOW_TRANSPARENCY,
    exposure: toneMappingExposure,
    dirLightIntensity: dirLightIntencity,
    dirLightX: dirLight.position.x,
    dirLightY: dirLight.position.y,
    dirLightZ: dirLight.position.z,
    dirShadowNear: dirLight.shadow.camera.near,
    dirShadowFar: dirLight.shadow.camera.far,
    envMapIntensity: ENVIRONMENT_MAP_INTENSITY,
    fillFrontIntensity: fillLightFront.intensity,
    fillBackIntensity: fillLightBack.intensity,
    modelX: model.position.x,
    modelY: model.position.y,
    modelZ: model.position.z,
    modelRotY: model.rotation.y,
    cameraFov: camera.fov,
  };

  const lightFolder = gui.addFolder('Directional Light');
  lightFolder.add(guiParams, 'dirLightIntensity', 0, 30, 0.1).name('Intensity').onChange(v => { dirLight.intensity = v; requestRender(); });
  lightFolder.add(guiParams, 'dirLightX', -100, 100, 0.5).name('X').onChange(v => { dirLight.position.x = v; requestRender(); });
  lightFolder.add(guiParams, 'dirLightY', -100, 100, 0.5).name('Y').onChange(v => { dirLight.position.y = v; requestRender(); });
  lightFolder.add(guiParams, 'dirLightZ', -100, 100, 0.5).name('Z').onChange(v => { dirLight.position.z = v; requestRender(); });
  lightFolder.add(guiParams, 'dirShadowNear', 0.01, 10, 0.01).name('Shadow Near').onChange(v => { dirLight.shadow.camera.near = v; dirLight.shadow.camera.updateProjectionMatrix(); requestRender(); });
  lightFolder.add(guiParams, 'dirShadowFar', 1, 500, 1).name('Shadow Far').onChange(v => { dirLight.shadow.camera.far = v; dirLight.shadow.camera.updateProjectionMatrix(); requestRender(); });

  const fillFolder = gui.addFolder('Fill Lights');
  fillFolder.add(guiParams, 'fillFrontIntensity', 0, 30, 0.1).name('Front Intensity').onChange(v => { fillLightFront.intensity = v; requestRender(); });
  fillFolder.add(guiParams, 'fillBackIntensity', 0, 30, 0.1).name('Back Intensity').onChange(v => { fillLightBack.intensity = v; requestRender(); });

  const envFolder = gui.addFolder('Environment');
  envFolder.add(guiParams, 'exposure', 0, 5, 0.05).name('Exposure').onChange(v => { renderer.toneMappingExposure = v; requestRender(); });
  envFolder.add(guiParams, 'envMapIntensity', 0, 5, 0.05).name('EnvMap Intensity').onChange(v => { scene.traverse(o => { if (o.isMesh && o.material.envMap) { o.material.envMapIntensity = v; o.material.needsUpdate = true; } }); requestRender(); });
  envFolder.add(guiParams, 'shadowTransparency', 0, 1, 0.01).name('Shadow Opacity').onChange(v => { floorMaterial.opacity = v; requestRender(); });

  const modelFolder = gui.addFolder('Model Position');
  modelFolder.add(guiParams, 'modelX', -5, 5, 0.01).name('X').onChange(v => { model.position.x = v; requestRender(); });
  modelFolder.add(guiParams, 'modelY', -5, 5, 0.01).name('Y').onChange(v => { model.position.y = v; requestRender(); });
  modelFolder.add(guiParams, 'modelZ', -5, 5, 0.01).name('Z').onChange(v => { model.position.z = v; requestRender(); });
  modelFolder.add(guiParams, 'modelRotY', -Math.PI, Math.PI, 0.01).name('Rotation Y').onChange(v => { model.rotation.y = v; requestRender(); });

  const cameraFolder = gui.addFolder('Camera');
  cameraFolder.add(guiParams, 'cameraFov', 10, 120, 1).name('FOV').onChange(v => { camera.fov = v; camera.updateProjectionMatrix(); requestRender(); });
}

//#endregion
